import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Placeholder } from './styles';

/**
 * Defers mounting its children until the wrapper scrolls into (or near) the viewport.
 * Once mounted, children stay mounted so any work they performed (e.g. downloading
 * attachment blobs) is not repeated when scrolling away and back.
 */
const LazyMountWrapper = ({ children, rootMargin }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) {
      return undefined;
    }

    const node = ref.current;
    // Fall back to rendering immediately where IntersectionObserver is unavailable.
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin });

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  if (isVisible) {
    return children;
  }

  return <Placeholder ref={ref} />;
};

LazyMountWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  rootMargin: PropTypes.string,
};

LazyMountWrapper.defaultProps = {
  rootMargin: '300px', // Start loading slightly before the element enters the viewport.
};

export default LazyMountWrapper;
